// Percorso: src/pages/enrolLingue.js
//
// IL DIZIONARIO DELLE TRE PAGINE PUBBLICHE — UNA SOLA FONTE (cantiere
// «enrollment», cantiere 2: le lingue d'aiuto).
//
// Le tre pagine in perimetro — /enrol (scelta, ramo adulto, ramo famiglia)
// e /login — servono il loro testo IN INGLESE, e sotto ogni etichetta
// inglese possono mostrare una RIGA D'AIUTO in arabo, cinese o hindi.
// L'inglese non sparisce mai: l'aiuto si aggiunge, non sostituisce.
//
// ⚠️ QUESTO FILE È GENERATO dal riferimento congelato
// docs/cantieri/enroll/ENROLL-2-DIZIONARIO-TRADUZIONI.md: le celle sono
// state COPIATE, non ribattute. Una correzione di traduzione si fa nel
// .md e si rigenera, così le due sedi non divergono.
//
// ⚠️ L'INGLESE DI QUESTO FILE NON È TESTO NUOVO. È, alla lettera, quello
// che le pagine già mostravano prima del cantiere: il confronto è
// meccanico (vedi il referto). L'unica stringa inglese NUOVA è
// `lang.notice`, la riga d'avviso dell'interruttore, che viene dal
// mockup approvato e non dal dizionario.
//
// ⚠️ CHE COSA NON STA QUI, e non per dimenticanza:
//   • le POLICY (i quattro documenti e la formula che li introduce):
//     restano in inglese in ogni lingua — sono la formula legale, e i PDF
//     del club esistono solo in inglese. Vivono in `LinkTermini`
//     (enrolShared.jsx) e non si toccano;
//   • `common.note.noPassword` («No password is required… you will
//     receive by email the instructions…»): il codice stesso la dichiara
//     falsa. Prima si corregge l'inglese, poi la si traduce. Resta dov'è;
//   • i segnaposto dei campi, le frecce, la ✕, i <strong> e i <br>: i dati
//     si scrivono in inglese e il markup sta sull'inglese. L'aiuto traduce
//     parole, non segni;
//   • gli errori che arrivano dal BACKEND (400/409): le loro parole vivono
//     in backend/server.js, fuori da questo perimetro. Si mostrano come
//     arrivano, in inglese;
//   • FinishLogin e il rinnovo stagionale: fuori perimetro, in inglese.
//
// ⚠️ TRE CHIAVI HANNO `en: null`, ed è deliberato: il loro inglese porta
// dentro del markup (<strong>PENDING</strong>) e vive nella pagina. Qui
// c'è solo l'aiuto. `testo()` su quelle chiavi restituisce "" e la pagina
// non la chiama mai: usa `<Aiuto>`, che legge solo le tre lingue.

/**
 * LE TRE LINGUE D'AIUTO, nel loro alfabeto e nel loro ordine.
 *
 * ⚠️ NON C'È UNA VOCE «English», e non è una svista: l'inglese è la base
 * fissa della pagina, non una scelta fra quattro. L'interruttore accende e
 * spegne un AIUTO.
 */
export const LINGUE = [
  { codice: "ar", nome: "العربية", rtl: true },
  { codice: "zh", nome: "中文", rtl: false },
  { codice: "hi", nome: "हिन्दी", rtl: false },
];

/** I soli codici ammessi come lingua d'aiuto attiva (oltre a null). */
export const CODICI_LINGUA = LINGUE.map((l) => l.codice);

/**
 * IL DIZIONARIO. Una riga per chiave: l'inglese servito di default e le
 * tre righe d'aiuto. Una casella vuota ("") significa «non tradotta»: la
 * riga d'aiuto semplicemente non compare, e l'inglese resta.
 */
export const TESTI = {
  "field.firstName": {
    en: "First Name*",
    ar: "الاسم الأول*",
    zh: "名字*",
    hi: "पहला नाम*",
  },
  "field.lastName": {
    en: "Last Name*",
    ar: "اسم العائلة*",
    zh: "姓氏*",
    hi: "अंतिम नाम*",
  },
  "field.email": {
    en: "Email*",
    ar: "البريد الإلكتروني*",
    zh: "电子邮箱*",
    hi: "ईमेल*",
  },
  "field.emailLogin": {
    en: "Email",
    ar: "البريد الإلكتروني",
    zh: "电子邮箱",
    hi: "ईमेल",
  },
  "field.dob": {
    en: "Date of Birth*",
    ar: "تاريخ الميلاد*",
    zh: "出生日期*",
    hi: "जन्म तिथि*",
  },
  "field.gender": {
    en: "Gender*",
    ar: "الجنس*",
    zh: "性别*",
    hi: "लिंग*",
  },
  "field.role": {
    en: "Role*",
    ar: "الصفة*",
    zh: "身份*",
    hi: "भूमिका*",
  },
  "opt.select": {
    en: "Select",
    ar: "اختر",
    zh: "请选择",
    hi: "चुनें",
  },
  "opt.male": {
    en: "Male",
    ar: "ذكر",
    zh: "男",
    hi: "पुरुष",
  },
  "opt.female": {
    en: "Female",
    ar: "أنثى",
    zh: "女",
    hi: "महिला",
  },
  "opt.player": {
    en: "Player",
    ar: "لاعب",
    zh: "球员",
    hi: "खिलाड़ी",
  },
  "opt.coach": {
    en: "Coach",
    ar: "مدرب",
    zh: "教练",
    hi: "कोच",
  },
  "btn.backToSignIn": {
    en: "Back to sign in",
    ar: "العودة إلى تسجيل الدخول",
    zh: "返回登录",
    hi: "साइन इन पर वापस जाएँ",
  },
  "btn.chooseOther": {
    en: "Choose a different option",
    ar: "اختر خيارًا آخر",
    zh: "选择其他选项",
    hi: "कोई दूसरा विकल्प चुनें",
  },
  "btn.gotoFamily": {
    en: "Go to the family enrolment form",
    ar: "الانتقال إلى نموذج تسجيل العائلة",
    zh: "前往家庭报名表",
    hi: "फ़ैमिली एनरोलमेंट फ़ॉर्म पर जाएँ",
  },
  "btn.send": {
    en: "Send enrolment",
    ar: "إرسال التسجيل",
    zh: "提交报名",
    hi: "नामांकन भेजें",
  },
  "btn.sending": {
    en: "Sending...",
    ar: "جارٍ الإرسال...",
    zh: "正在发送...",
    hi: "भेजा जा रहा है...",
  },
  "btn.backToSignInPage": {
    en: "Back to the sign-in page",
    ar: "العودة إلى صفحة تسجيل الدخول",
    zh: "返回登录页面",
    hi: "साइन-इन पेज पर वापस जाएँ",
  },
  "err.unreachable": {
    en: "We could not reach the club server. Your enrolment has NOT been sent. Please check your connection and try again.",
    ar: "تعذّر الوصول إلى خادم النادي. لم يتم إرسال تسجيلك. يُرجى التحقق من اتصالك والمحاولة مرة أخرى.",
    zh: "无法连接到俱乐部服务器。您的报名尚未发送。请检查您的网络连接后重试。",
    hi: "हम क्लब सर्वर तक नहीं पहुँच सके। आपका नामांकन नहीं भेजा गया है। कृपया अपना कनेक्शन जाँचें और पुनः प्रयास करें।",
  },
  "err.emailTaken": {
    en: "This email address is already registered. If it is yours, contact the club.",
    ar: "هذا البريد الإلكتروني مُسجَّل بالفعل. إذا كان لك، فيُرجى الاتصال بالنادي.",
    zh: "该电子邮箱已注册。如果是您的，请联系俱乐部。",
    hi: "यह ईमेल पता पहले से पंजीकृत है। यदि यह आपका है, तो कृपया क्लब से संपर्क करें।",
  },
  "err.badDetails": {
    en: "Some of the details you entered were not accepted.",
    ar: "بعض البيانات التي أدخلتها لم تُقبَل.",
    zh: "您输入的部分信息未被接受。",
    hi: "आपके द्वारा दर्ज कुछ विवरण स्वीकार नहीं किए गए।",
  },
  "err.serverFail": {
    en: "The club server could not complete your enrolment. Your enrolment has NOT been recorded. Please try again later, or contact the club.",
    ar: "تعذّر على خادم النادي إتمام تسجيلك. لم يُسجَّل طلبك. يُرجى المحاولة لاحقًا أو الاتصال بالنادي.",
    zh: "俱乐部服务器无法完成您的报名。您的报名尚未记录。请稍后重试，或联系俱乐部。",
    hi: "क्लब सर्वर आपका नामांकन पूरा नहीं कर सका। आपका नामांकन दर्ज नहीं हुआ है। कृपया बाद में पुनः प्रयास करें, या क्लब से संपर्क करें।",
  },
  "enrol.title": {
    en: "Enrol with Queens Park SC",
    ar: "التسجيل في Queens Park SC",
    zh: "在 Queens Park SC 报名",
    hi: "Queens Park SC में नामांकन करें",
  },
  "enrol.choice.group": {
    en: "Who are you enrolling?",
    ar: "من الذي تُسجّله؟",
    zh: "您在为谁报名？",
    hi: "आप किसका नामांकन कर रहे हैं?",
  },
  "enrol.choice.self.title": {
    en: "I am enrolling myself",
    ar: "أُسجّل نفسي",
    zh: "我为自己报名",
    hi: "मैं स्वयं का नामांकन कर रहा/रही हूँ",
  },
  "enrol.choice.self.help": {
    en: "Choose this if you are 18 or over and you are enrolling yourself as a player or as a coach.",
    ar: "اختر هذا إذا كان عمرك 18 عامًا أو أكثر وتُسجّل نفسك كلاعب أو مدرب.",
    zh: "如果您年满 18 岁，并为自己以球员或教练身份报名，请选择此项。",
    hi: "यदि आपकी आयु 18 वर्ष या अधिक है और आप स्वयं को खिलाड़ी या कोच के रूप में नामांकित कर रहे हैं, तो यह चुनें।",
  },
  "enrol.choice.children.title": {
    en: "I am enrolling one or more children of my family",
    ar: "أُسجّل طفلًا واحدًا أو أكثر من عائلتي",
    zh: "我为家中的一名或多名孩子报名",
    hi: "मैं अपने परिवार के एक या अधिक बच्चों का नामांकन कर रहा/रही हूँ",
  },
  "enrol.choice.children.help": {
    en: "Choose this if you are the parent or guardian of one or more children under 18. You are enrolled too, as their parent or guardian, and you accept the club policies on their behalf.",
    ar: "اختر هذا إذا كنت والدًا أو وليَّ أمر لطفل واحد أو أكثر دون سن 18. سيتم تسجيلك أيضًا بصفتك والدهم أو وليَّ أمرهم، وتوافق على سياسات النادي نيابةً عنهم.",
    zh: "如果您是一名或多名 18 岁以下孩子的家长或监护人，请选择此项。您也会作为其家长或监护人一同报名，并代表他们接受俱乐部的各项政策。",
    hi: "यदि आप 18 वर्ष से कम आयु के एक या अधिक बच्चों के माता-पिता या अभिभावक हैं, तो यह चुनें। आपका भी उनके माता-पिता/अभिभावक के रूप में नामांकन होगा, और आप उनकी ओर से क्लब की नीतियों को स्वीकार करते हैं।",
  },
  "enrol.self.intro": {
    en: "For players and coaches aged 18 or over, enrolling themselves. Under 18s are enrolled by a parent or guardian from the family enrolment form.",
    ar: "للاعبين والمدربين بعمر 18 عامًا فأكثر الذين يُسجّلون أنفسهم. أمّا من هم دون 18 عامًا فيُسجّلهم أحد الوالدين أو وليّ الأمر من نموذج تسجيل العائلة.",
    zh: "适用于年满 18 岁、为自己报名的球员和教练。18 岁以下者由家长或监护人通过家庭报名表报名。",
    hi: "18 वर्ष या उससे अधिक आयु के खिलाड़ियों और कोचों के लिए, जो स्वयं का नामांकन कर रहे हैं। 18 वर्ष से कम आयु वालों का नामांकन माता-पिता या अभिभावक द्वारा फ़ैमिली एनरोलमेंट फ़ॉर्म से किया जाता है।",
  },
  "enrol.self.err.required": {
    en: "Please fill in all the fields.",
    ar: "يُرجى ملء جميع الحقول.",
    zh: "请填写所有字段。",
    hi: "कृपया सभी फ़ील्ड भरें।",
  },
  "enrol.self.err.email": {
    en: "Please enter a valid email address.",
    ar: "يُرجى إدخال بريد إلكتروني صالح.",
    zh: "请输入有效的电子邮箱。",
    hi: "कृपया एक मान्य ईमेल पता दर्ज करें।",
  },
  "enrol.self.err.gender": {
    en: "Please select a gender.",
    ar: "يُرجى اختيار الجنس.",
    zh: "请选择性别。",
    hi: "कृपया लिंग चुनें।",
  },
  "enrol.self.err.role": {
    en: "Please select a role.",
    ar: "يُرجى اختيار الصفة.",
    zh: "请选择身份。",
    hi: "कृपया भूमिका चुनें।",
  },
  "enrol.self.err.dob": {
    en: "Please enter a valid date of birth.",
    ar: "يُرجى إدخال تاريخ ميلاد صالح.",
    zh: "请输入有效的出生日期。",
    hi: "कृपया एक मान्य जन्म तिथि दर्ज करें।",
  },
  "enrol.self.err.dobFuture": {
    en: "The date of birth cannot be in the future.",
    ar: "لا يمكن أن يكون تاريخ الميلاد في المستقبل.",
    zh: "出生日期不能是将来的日期。",
    hi: "जन्म तिथि भविष्य में नहीं हो सकती।",
  },
  "enrol.self.err.minor": {
    en: "Under 18: you cannot enrol yourself. A parent or guardian must enrol you from the family enrolment form.",
    ar: "أقل من 18 عامًا: لا يمكنك تسجيل نفسك. يجب أن يسجّلك أحد الوالدين أو وليّ الأمر من نموذج تسجيل العائلة.",
    zh: "未满 18 岁：您不能为自己报名。须由家长或监护人通过家庭报名表为您报名。",
    hi: "18 से कम: आप स्वयं का नामांकन नहीं कर सकते। माता-पिता या अभिभावक को फ़ैमिली एनरोलमेंट फ़ॉर्म से आपका नामांकन करना होगा।",
  },
  "err.terms": {
    en: "You must accept the Terms & Policies.",
    ar: "يجب أن توافق على الشروط والسياسات.",
    zh: "您必须接受条款和政策。",
    hi: "आपको नियम और नीतियाँ स्वीकार करनी होंगी।",
  },
  "done.title": {
    en: "Enrolment received",
    ar: "تم استلام التسجيل",
    zh: "已收到报名",
    hi: "नामांकन प्राप्त हुआ",
  },
  "enrol.self.done.thanks": {
    en: "Thank you — we have received your enrolment request.",
    ar: "شكرًا لك — لقد استلمنا طلب تسجيلك.",
    zh: "谢谢 — 我们已收到您的报名申请。",
    hi: "धन्यवाद — हमें आपका नामांकन अनुरोध प्राप्त हो गया है।",
  },
  "enrol.self.done.pending": {
    en: null,
    ar: "تسجيلك الآن قيد المراجعة: سيقوم أحد مسؤولي النادي بمراجعته. يمكنك تسجيل الدخول الآن بعنوان البريد الإلكتروني الذي أدخلته — وإلى أن تتم الموافقة على تسجيلك سترى شاشة انتظار.",
    zh: "您的报名现在处于待审核状态：俱乐部工作人员将进行审核。您现在可以使用所填写的邮箱登录 — 在报名获批之前，您将看到一个等待页面。",
    hi: "आपका नामांकन अब लंबित है: क्लब का एक अधिकारी इसकी समीक्षा करेगा। आप अभी दर्ज किए गए ईमेल पते से साइन इन कर सकते हैं — जब तक आपका नामांकन स्वीकृत नहीं हो जाता, आपको एक प्रतीक्षा स्क्रीन दिखाई देगी।",
  },
  "enrol.self.done.mail": {
    en: "A confirmation of this request, with the terms you accepted, has been sent to that address.",
    ar: "تم إرسال تأكيد لهذا الطلب، مع الشروط التي وافقت عليها، إلى ذلك العنوان.",
    zh: "本次申请的确认信，连同您已接受的条款，已发送至该邮箱。",
    hi: "इस अनुरोध की पुष्टि, आपके द्वारा स्वीकार की गई शर्तों सहित, उस पते पर भेज दी गई है।",
  },
  "done.ref": {
    en: "Reference: {id}",
    ar: "المرجع: {id}",
    zh: "参考编号：{id}",
    hi: "संदर्भ: {id}",
  },
  "done.refQuote": {
    en: "Please quote it if you contact the club about this enrolment.",
    ar: "يُرجى ذكره إذا اتصلت بالنادي بخصوص هذا التسجيل.",
    zh: "如就此报名联系俱乐部，请提供该编号。",
    hi: "यदि आप इस नामांकन के बारे में क्लब से संपर्क करें तो कृपया इसका उल्लेख करें।",
  },
  "family.title": {
    en: "Family enrolment",
    ar: "تسجيل العائلة",
    zh: "家庭报名",
    hi: "फ़ैमिली नामांकन",
  },
  "family.intro": {
    en: "For a parent or guardian enrolling one or more children under 18. You are enrolled too, as their parent/guardian.",
    ar: "لأحد الوالدين أو وليّ الأمر الذي يسجّل طفلًا واحدًا أو أكثر دون سن 18. سيتم تسجيلك أيضًا بصفتك والدهم/وليَّ أمرهم.",
    zh: "供家长或监护人为一名或多名 18 岁以下孩子报名。您也会作为其家长/监护人一同报名。",
    hi: "उन माता-पिता या अभिभावक के लिए जो 18 वर्ष से कम आयु के एक या अधिक बच्चों का नामांकन कर रहे हैं। आपका भी उनके माता-पिता/अभिभावक के रूप में नामांकन होगा।",
  },
  "family.alreadyEnrolled": {
    en: "The children I look after have already been enrolled by another parent or guardian — the club will link them to me.",
    ar: "الأطفال الذين أرعاهم قد سجّلهم بالفعل أحد الوالدين أو وليّ أمر آخر — سيقوم النادي بربطهم بي.",
    zh: "我照顾的孩子已由另一位家长或监护人报名 — 俱乐部会将他们关联到我名下。",
    hi: "जिन बच्चों की मैं देखभाल करता/करती हूँ, उनका नामांकन किसी अन्य माता-पिता या अभिभावक द्वारा पहले ही किया जा चुका है — क्लब उन्हें मुझसे जोड़ देगा।",
  },
  "family.parent.section": {
    en: "Parent / guardian",
    ar: "الوالد / وليّ الأمر",
    zh: "家长 / 监护人",
    hi: "माता-पिता / अभिभावक",
  },
  "family.parent.email.note": {
    en: "All club communications, for you and for the children, are sent to this address. The children do not need an email address.",
    ar: "تُرسَل جميع مراسلات النادي، لك وللأطفال، إلى هذا العنوان. لا يحتاج الأطفال إلى بريد إلكتروني.",
    zh: "所有俱乐部通讯，无论是给您还是给孩子的，都会发送到此邮箱。孩子无需电子邮箱。",
    hi: "क्लब के सभी संदेश, आपके और बच्चों दोनों के लिए, इसी पते पर भेजे जाते हैं। बच्चों को ईमेल पते की आवश्यकता नहीं है।",
  },
  "family.children.section": {
    en: "Children under 18",
    ar: "الأطفال دون سن 18",
    zh: "18 岁以下的孩子",
    hi: "18 वर्ष से कम आयु के बच्चे",
  },
  "family.child.heading": {
    en: "Child {n}",
    ar: "الطفل {n}",
    zh: "第 {n} 名孩子",
    hi: "बच्चा {n}",
  },
  "family.alsoMe.check": {
    en: "I play or coach at the club myself, as well as being a parent.",
    ar: "أُمارس اللعب أو التدريب في النادي بنفسي، إضافةً إلى كوني أحد الوالدين.",
    zh: "除了作为家长，我本人也在俱乐部踢球或执教。",
    hi: "माता-पिता होने के साथ-साथ, मैं स्वयं भी क्लब में खेलता/खेलती या कोचिंग करता/करती हूँ।",
  },
  "family.alsoMe.label": {
    en: "I take part as*",
    ar: "أشارك بصفة*",
    zh: "我的参与身份*",
    hi: "मैं इस रूप में भाग लेता/लेती हूँ*",
  },
  "family.alsoMe.note": {
    en: "This is a request: the club will confirm it when your enrolment is reviewed.",
    ar: "هذا طلب: سيؤكّده النادي عند مراجعة تسجيلك.",
    zh: "这是一项申请：俱乐部将在审核您的报名时予以确认。",
    hi: "यह एक अनुरोध है: क्लब आपके नामांकन की समीक्षा के समय इसकी पुष्टि करेगा।",
  },
  "family.child.add": {
    en: "Add another child",
    ar: "إضافة طفل آخر",
    zh: "添加另一名孩子",
    hi: "एक और बच्चा जोड़ें",
  },
  "family.child.remove": {
    en: "Remove child {n}",
    ar: "إزالة الطفل {n}",
    zh: "移除第 {n} 名孩子",
    hi: "बच्चा {n} हटाएँ",
  },
  "family.child.maxReached": {
    en: "You can enrol up to {MAX} children in a single form. If you need more, send this form and then contact the club.",
    ar: "يمكنك تسجيل حتى {MAX} أطفال في نموذج واحد. إذا احتجت إلى المزيد، أرسِل هذا النموذج ثم اتصل بالنادي.",
    zh: "您在一份表单中最多可为 {MAX} 名孩子报名。如需更多，请先提交此表单，然后联系俱乐部。",
    hi: "आप एक ही फ़ॉर्म में अधिकतम {MAX} बच्चों का नामांकन कर सकते हैं। यदि अधिक चाहिए, तो यह फ़ॉर्म भेजें और फिर क्लब से संपर्क करें।",
  },
  "family.err.parentRequired": {
    en: "Please fill in all the parent/guardian fields.",
    ar: "يُرجى ملء جميع حقول الوالد/وليّ الأمر.",
    zh: "请填写所有家长/监护人字段。",
    hi: "कृपया माता-पिता/अभिभावक के सभी फ़ील्ड भरें।",
  },
  "family.err.parentEmail": {
    en: "Please enter a valid email address for the parent/guardian.",
    ar: "يُرجى إدخال بريد إلكتروني صالح للوالد/وليّ الأمر.",
    zh: "请为家长/监护人输入有效的电子邮箱。",
    hi: "कृपया माता-पिता/अभिभावक के लिए एक मान्य ईमेल पता दर्ज करें।",
  },
  "family.err.parentGender": {
    en: "Please select the parent/guardian gender.",
    ar: "يُرجى اختيار جنس الوالد/وليّ الأمر.",
    zh: "请选择家长/监护人的性别。",
    hi: "कृपया माता-पिता/अभिभावक का लिंग चुनें।",
  },
  "family.err.context": {
    en: "Please choose whether you play or coach yourself.",
    ar: "يُرجى اختيار ما إذا كنت تلعب أو تدرّب بنفسك.",
    zh: "请选择您本人是踢球还是执教。",
    hi: "कृपया चुनें कि आप स्वयं खेलते हैं या कोचिंग करते हैं।",
  },
  "family.err.noChildren": {
    en: "Please add at least one child, or tick the box above if they have already been enrolled by another parent or guardian.",
    ar: "يُرجى إضافة طفل واحد على الأقل، أو ضع علامة في المربع أعلاه إذا كان قد سجّلهم بالفعل أحد الوالدين أو وليّ أمر آخر.",
    zh: "请至少添加一名孩子；如果他们已由另一位家长或监护人报名，请勾选上方的方框。",
    hi: "कृपया कम से कम एक बच्चा जोड़ें, या यदि उनका नामांकन किसी अन्य माता-पिता या अभिभावक द्वारा पहले ही किया जा चुका है तो ऊपर दिए गए बॉक्स पर टिक करें।",
  },
  "family.err.tooManyChildren": {
    en: "You can enrol at most {MAX} children in a single form.",
    ar: "يمكنك تسجيل ما لا يزيد عن {MAX} أطفال في نموذج واحد.",
    zh: "您在一份表单中最多可为 {MAX} 名孩子报名。",
    hi: "आप एक ही फ़ॉर्म में अधिकतम {MAX} बच्चों का नामांकन कर सकते हैं।",
  },
  "family.err.parentEmailTaken": {
    en: "The parent/guardian email address is already registered. If it is yours, please do NOT enrol a second time: contact the club and we will add the children to your existing record.",
    ar: "عنوان البريد الإلكتروني للوالد/وليّ الأمر مُسجَّل بالفعل. إذا كان لك، فيُرجى عدم التسجيل مرة ثانية: اتصل بالنادي وسنضيف الأطفال إلى سجلك الحالي.",
    zh: "该家长/监护人邮箱已注册。如果是您的，请勿再次报名：请联系俱乐部，我们会将孩子添加到您现有的记录中。",
    hi: "माता-पिता/अभिभावक का ईमेल पता पहले से पंजीकृत है। यदि यह आपका है, तो कृपया दूसरी बार नामांकन न करें: क्लब से संपर्क करें और हम बच्चों को आपके मौजूदा रिकॉर्ड में जोड़ देंगे।",
  },
  "family.err.childPrefix": {
    en: "Child {n}: ",
    ar: "الطفل {n}: ",
    zh: "第 {n} 名孩子：",
    hi: "बच्चा {n}: ",
  },
  "family.err.childRequired": {
    en: "please fill in all the fields.",
    ar: "يُرجى ملء جميع الحقول.",
    zh: "请填写所有字段。",
    hi: "कृपया सभी फ़ील्ड भरें।",
  },
  "family.err.childGender": {
    en: "please select a gender.",
    ar: "يُرجى اختيار الجنس.",
    zh: "请选择性别。",
    hi: "कृपया लिंग चुनें।",
  },
  "family.err.childDob": {
    en: "please enter a valid date of birth.",
    ar: "يُرجى إدخال تاريخ ميلاد صالح.",
    zh: "请输入有效的出生日期。",
    hi: "कृपया एक मान्य जन्म तिथि दर्ज करें।",
  },
  "family.err.childDobFuture": {
    en: "the date of birth cannot be in the future.",
    ar: "لا يمكن أن يكون تاريخ الميلاد في المستقبل.",
    zh: "出生日期不能是将来的日期。",
    hi: "जन्म तिथि भविष्य में नहीं हो सकती।",
  },
  "family.err.childAdult": {
    en: "this date of birth is of an adult (18 or over). Adults enrol themselves from the individual form.",
    ar: "تاريخ الميلاد هذا لشخص بالغ (18 عامًا أو أكثر). يسجّل البالغون أنفسهم من النموذج الفردي.",
    zh: "此出生日期属于成年人（18 岁或以上）。成年人须通过个人报名表自行报名。",
    hi: "यह जन्म तिथि एक वयस्क (18 वर्ष या अधिक) की है। वयस्क व्यक्तिगत फ़ॉर्म से स्वयं का नामांकन करते हैं।",
  },
  "family.err.childTerms": {
    en: "you must accept the Terms & Policies on their behalf.",
    ar: "يجب أن توافق على الشروط والسياسات نيابةً عنه.",
    zh: "您必须代表其接受条款和政策。",
    hi: "आपको उनकी ओर से नियम और नीतियाँ स्वीकार करनी होंगी।",
  },
  "family.done.thanks": {
    en: "Thank you — we have received your family enrolment request.",
    ar: "شكرًا لك — لقد استلمنا طلب تسجيل عائلتك.",
    zh: "谢谢 — 我们已收到您的家庭报名申请。",
    hi: "धन्यवाद — हमें आपका फ़ैमिली नामांकन अनुरोध प्राप्त हो गया है।",
  },
  "family.done.mail": {
    en: "A confirmation of this request, with the terms you accepted — for yourself and on behalf of the children — has been sent to that address.",
    ar: "تم إرسال تأكيد لهذا الطلب، مع الشروط التي وافقت عليها — لنفسك ونيابةً عن الأطفال — إلى ذلك العنوان.",
    zh: "本次申请的确认信，连同您已接受的条款 — 为您本人并代表孩子 — 已发送至该邮箱。",
    hi: "इस अनुरोध की पुष्टि, आपके द्वारा स्वीकार की गई शर्तों सहित — अपने लिए और बच्चों की ओर से — उस पते पर भेज दी गई है।",
  },
  "login.title": {
    en: "QPSC Login",
    ar: "تسجيل الدخول إلى QPSC",
    zh: "QPSC 登录",
    hi: "QPSC लॉगिन",
  },
  "login.submit": {
    en: "Send me the link",
    ar: "أرسل لي الرابط",
    zh: "给我发送登录链接",
    hi: "मुझे लिंक भेजें",
  },
  "login.submit.sending": {
    en: "Sending...",
    ar: "جارٍ الإرسال...",
    zh: "正在发送...",
    hi: "भेजा जा रहा है...",
  },
  "login.sent": {
    en: "If this address is enrolled, we have sent you a link to sign in. Please check your email.",
    ar: "إذا كان هذا العنوان مُسجَّلًا، فقد أرسلنا إليك رابطًا لتسجيل الدخول. يُرجى التحقق من بريدك الإلكتروني.",
    zh: "如果该邮箱已注册，我们已向您发送登录链接。请查收您的电子邮件。",
    hi: "यदि यह पता नामांकित है, तो हमने आपको साइन इन करने के लिए एक लिंक भेज दिया है। कृपया अपना ईमेल जाँचें।",
  },
  "login.err.send": {
    en: "We could not send the link. Please check the address and try again.",
    ar: "تعذّر إرسال الرابط. يُرجى التحقق من العنوان والمحاولة مرة أخرى.",
    zh: "无法发送链接。请检查邮箱地址后重试。",
    hi: "हम लिंक नहीं भेज सके। कृपया पता जाँचें और पुनः प्रयास करें।",
  },
  "login.err.notEnrolled": {
    en: "This email address isn't enrolled yet.",
    ar: "هذا البريد الإلكتروني غير مُسجَّل بعد.",
    zh: "该电子邮箱尚未注册。",
    hi: "यह ईमेल पता अभी नामांकित नहीं है।",
  },
  "login.err.ambiguous": {
    en: "There's a problem with this address. Please contact the club.",
    ar: "توجد مشكلة في هذا العنوان. يُرجى الاتصال بالنادي.",
    zh: "该邮箱地址存在问题。请联系俱乐部。",
    hi: "इस पते में कोई समस्या है। कृपया क्लब से संपर्क करें।",
  },
  "login.err.profile": {
    en: "We couldn't load your profile. Please try again.",
    ar: "تعذّر تحميل ملفك الشخصي. يُرجى المحاولة مرة أخرى.",
    zh: "无法加载您的资料。请重试。",
    hi: "हम आपकी प्रोफ़ाइल लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
  },
  "login.retry": {
    en: "Retry",
    ar: "إعادة المحاولة",
    zh: "重试",
    hi: "पुनः प्रयास करें",
  },
  "login.useOtherEmail": {
    en: "Use a different email address",
    ar: "استخدام عنوان بريد إلكتروني آخر",
    zh: "使用其他邮箱",
    hi: "कोई दूसरा ईमेल पता उपयोग करें",
  },
  "login.enrolLink": {
    en: "Not enrolled yet? ENROL NOW",
    ar: "لست مُسجَّلًا بعد؟ سجّل الآن",
    zh: "还没有报名？立即报名",
    hi: "अभी तक नामांकित नहीं? अभी नामांकन करें",
  },
  "family.done.pending.withChildren": {
    en: null,
    ar: "تسجيلك الآن قيد المراجعة: سيقوم أحد مسؤولي النادي بمراجعته. لقد سجّلت نفسك و{n} من الأطفال. يمكنك تسجيل الدخول الآن ببريدك الإلكتروني — وإلى أن تتم الموافقة على تسجيلك سترى شاشة انتظار. لا يسجّل الأطفال الدخول بأنفسهم: أنت تستخدم التطبيق نيابةً عنهم.",
    zh: "您的报名现在处于待审核状态：俱乐部工作人员将进行审核。您已为自己和 {n} 名孩子报名。您现在可以使用自己的邮箱登录 — 在报名获批之前，您将看到一个等待页面。孩子不自行登录：由您代表他们使用应用。",
    hi: "आपका नामांकन अब लंबित है: क्लब का एक अधिकारी इसकी समीक्षा करेगा। आपने स्वयं का और {n} बच्चे/बच्चों का नामांकन किया। आप अभी अपने ईमेल पते से साइन इन कर सकते हैं — जब तक आपका नामांकन स्वीकृत नहीं हो जाता, आपको एक प्रतीक्षा स्क्रीन दिखाई देगी। बच्चे स्वयं साइन इन नहीं करते: आप उनकी ओर से ऐप का उपयोग करते हैं।",
  },
  "family.done.pending.selfOnly": {
    en: null,
    ar: "تسجيلك الآن قيد المراجعة: سيقوم أحد مسؤولي النادي بمراجعته. لقد سجّلت نفسك فقط: سيربطك النادي بالأطفال المسجّلين بالفعل. يمكنك تسجيل الدخول الآن ببريدك الإلكتروني — وإلى أن تتم الموافقة على تسجيلك سترى شاشة انتظار. لا يسجّل الأطفال الدخول بأنفسهم: أنت تستخدم التطبيق نيابةً عنهم.",
    zh: "您的报名现在处于待审核状态：俱乐部工作人员将进行审核。您仅为自己报名：俱乐部会将您与已在册的孩子关联。您现在可以使用自己的邮箱登录 — 在报名获批之前，您将看到一个等待页面。孩子不自行登录：由您代表他们使用应用。",
    hi: "आपका नामांकन अब लंबित है: क्लब का एक अधिकारी इसकी समीक्षा करेगा। आपने केवल स्वयं का नामांकन किया: क्लब आपको पहले से दर्ज बच्चों से जोड़ देगा। आप अभी अपने ईमेल पते से साइन इन कर सकते हैं — जब तक आपका नामांकन स्वीकृत नहीं हो जाता, आपको एक प्रतीक्षा स्क्रीन दिखाई देगी। बच्चे स्वयं साइन इन नहीं करते: आप उनकी ओर से ऐप का उपयोग करते हैं।",
  },
  "family.child.termsOnBehalf": {
    en: "As parent or guardian, I accept the above on behalf of this child.",
    ar: "بصفتي والدًا أو وليَّ أمر، أوافق على ما ورد أعلاه نيابةً عن هذا الطفل.",
    zh: "作为家长或监护人，我代表此孩子接受上述内容。",
    hi: "माता-पिता या अभिभावक के रूप में, मैं इस बच्चे की ओर से उपरोक्त को स्वीकार करता/करती हूँ।",
  },
  "lang.notice": {
    en: "You can read this page in your language — please enter your details in English.",
    ar: "يمكنك قراءة هذه الصفحة بلغتك — يُرجى إدخال بياناتك بالإنجليزية.",
    zh: "您可以用您的语言阅读本页 — 请用英文填写您的信息。",
    hi: "आप इस पेज को अपनी भाषा में पढ़ सकते हैं — कृपया अपनी जानकारी अंग्रेज़ी में दर्ज करें।",
  },
  "lang.barLabel": {
    en: "Read the page in your language",
    ar: "",
    zh: "",
    hi: "",
  },
};

/**
 * L'inglese di una chiave, con gli interpolati sostituiti.
 *
 * I segnaposto sono `{n}`, `{id}`, `{MAX}`: restano quelli del
 * dizionario, e questa funzione li rimpiazza col valore passato. Una
 * chiave sconosciuta torna com'è scritta — si vede subito, e non sparisce
 * in silenzio.
 */
export function testo(chiave, valori) {
  const voce = TESTI[chiave];
  if (!voce) return chiave;
  if (voce.en == null) return "";
  return interpola(voce.en, valori);
}

/**
 * La riga d'aiuto di una chiave nella lingua indicata, o "" se non c'è
 * lingua attiva, se la chiave non esiste o se quella casella è vuota.
 */
export function aiuto(chiave, lingua, valori) {
  if (!lingua) return "";
  const voce = TESTI[chiave];
  if (!voce) return "";
  return interpola(voce[lingua] || "", valori);
}

/** Sostituisce {nome} col valore corrispondente. Senza valori, non tocca nulla. */
function interpola(stringa, valori) {
  if (!valori) return stringa;
  return stringa.replace(/\{(\w+)\}/g, (intero, nome) =>
    Object.prototype.hasOwnProperty.call(valori, nome) ? String(valori[nome]) : intero
  );
}
